import { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useDataStoreService } from '../context/DataStoreContext';
import { logger } from '../services/LoggerService';

export const useSignup = () => {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState(false);

    const { signup, logout, deleteUser } = useAuth();
    const { tutorProfileService } = useDataStoreService();

    const handleSignup = async (email: string, password: string, firstName: string, lastName: string) => {
        setLoading(true);
        setError('');
        try {
            logger.info('Starting signup process...');
            const user = await signup(email, password);
            logger.info('User created in Firebase:', user.uid);

            logger.info('Creating tutor profile...');
            await tutorProfileService.createTutorProfile(user.uid, {
                uid: user.uid,
                firstName,
                lastName,
                title: '',
                bio: '',
                rating: 0,
                numReviews: 0,
                price: 0,
                location: '',
                subjects: [],
            });
            logger.info('Tutor profile created successfully');

            // Non-critical step: Attempt to logout
            try {
                await logout();
                logger.info('Logout successful');
            } catch (logoutErr: any) {
                logger.warn('Logout after signup failed', logoutErr);
                // Continue execution - do not fail the signup process
            }

            logger.info('Setting success state to true');
            setSuccess(true);
        } catch (err: any) {
            logger.error('Signup failed', err);
            setError(`Signup failed: ${err.message}`);

            // ROLLBACK: Attempt to delete the user if profile creation failed
            // See docs/future-improvements/signup-atomicity.md for a more robust backend solution
            try {
                await deleteUser();
                logger.info('Rollback successful: User deleted');
            } catch (rollbackErr: any) {
                logger.error('Rollback failed', rollbackErr);
            }

            throw err; // Re-throw to let component handle specific UI side effects if needed
        } finally {
            setLoading(false);
        }
    };

    return {
        signup: handleSignup,
        loading,
        error,
        success,
        setError, // Expose setter to clear error from UI
        setSuccess // Expose setter if needed
    };
};
